import { BarChart3, ExternalLink, Terminal } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { getUmamiDashboardUrl } from "@/lib/umami";

/**
 * AnalyticsPage - Umami analytics dashboard link
 *
 * When Umami is configured: shows a card with link to the Umami dashboard.
 * When not configured: shows setup instructions.
 */
export function AnalyticsPage() {
  const { t } = useTranslation("dashboard");
  const dashboardUrl = getUmamiDashboardUrl();

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="space-y-2">
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">
          {t("pages.analytics.title")}
        </h1>
        <p className="text-base text-muted-foreground md:text-lg">
          {t("pages.analytics.description")}
        </p>
      </div>

      <Separator />

      {dashboardUrl ? <ConfiguredView url={dashboardUrl} /> : <UnconfiguredView />}
    </div>
  );
}

function ConfiguredView({ url }: { url: string }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <BarChart3 className="h-5 w-5" />
          Umami Analytics
        </CardTitle>
        <CardDescription>
          Privacy-first analytics powered by Umami. No cookies, no consent banners.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          View pageviews, visitors, referrers, and more in your Umami dashboard.
        </p>
        <Button asChild>
          <a href={url} target="_blank" rel="noopener noreferrer">
            Open Analytics Dashboard
            <ExternalLink className="ml-2 h-4 w-4" />
          </a>
        </Button>
      </CardContent>
    </Card>
  );
}

function UnconfiguredView() {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <BarChart3 className="h-5 w-5 text-muted-foreground" />
          Analytics Not Configured
        </CardTitle>
        <CardDescription>
          Set up Umami analytics to track pageviews and visitors for this project.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="rounded-lg border bg-muted/50 p-4">
          <div className="mb-2 flex items-center gap-2 text-sm font-medium">
            <Terminal className="h-4 w-4" />
            Quick setup
          </div>
          <code className="text-sm text-muted-foreground">bun run setup:analytics</code>
        </div>
        <p className="text-sm text-muted-foreground">
          This registers the project with your Umami instance and configures the tracking script.
          Analytics only load in production builds — no tracking in development.
        </p>
      </CardContent>
    </Card>
  );
}

export default AnalyticsPage;
